import './style.scss';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { HugerteEditor } from 'components/FormControl';
import ReactRouterPrompt from 'react-router-prompt';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  downloadDocxTemplate,
  downloadPdfTemplate,
  getReviewerInfo,
  createAcceptanceReview,
  getReviewDetail,
} from 'apis/acceptanceReviews.api';

import ErrorDialog from 'components/ErrorDialog';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';

import { DetailInfo } from 'features/FinancialReview/components';
import { EvaluationForm, EvaluateTable } from 'features/AcceptanceReview/components';
import { createAcceptanceReviewValidationSchema } from 'features/AcceptanceReview/validation';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import { is1593, isMemberAllowedReview } from 'utils/func';
import { validateSignature } from 'utils/validation/signatureValidation';
import Signature from 'features/PrequalificationReview/components/Signature';
import { TabPanel, TabView } from 'primereact/tabview';
import { ProjectProposalDefenseDetail } from 'features/ProjectProposalDefense/pages';
import { getSummaryReport, downloadFileReport } from 'apis/summaryReport';
import ReviewFormMidtermAcceptance from 'features/Project/components/ReviewFormMidtermAcceptance';
import DynamicAcceptanceForm from 'features/AcceptanceReview/components/DynamicAcceptanceReview';
import GeneralCouncilInfo from 'features/Council/components/GeneralInfo';

export default function AcceptanceReviewFormCreate() {
  // #region Data
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { fromInternal } = location.state || { fromInternal: false };
  const { isViewCouncilData } = location?.state || { isViewCouncilData: false };
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);

  const { projectProposalId } = useParams();
  const [searchParams] = useSearchParams();
  const councilId = searchParams.get('councilId');
  const councilMemberId = searchParams.get('councilMemberId');
  const downloadTemplateButtonRef = useRef();
  const summaryReportId = searchParams.get('summaryReportId');

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onChange', resolver: yupResolver(createAcceptanceReviewValidationSchema) });

  const { isDirty, dirtyFields } = useFormState({ control });

  const { ...formOptions } = useForm({
    mode: 'onChange',
  });

  const {
    mutate,
    error: errorRes,
    isError: isErrorRes,
    isLoading: isLoadingCreate,
  } = useMutation(createAcceptanceReview);

  const { data: _summaryReport } = useQuery(
    ['acceptanceSummaryReport', summaryReportId],
    () => getSummaryReport(summaryReportId),
    {
      enabled: summaryReportId !== null,
    }
  );

  const summaryReport = useMemo(() => _summaryReport?.data, [_summaryReport]);

  const { mutate: mutateDownloadFile } = useMutation(downloadFileReport);

  const handleDownloadFile = (filename, fileNameFormat = '') => {
    mutateDownloadFile(
      {
        summaryReportId,
        filename,
      },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = fileNameFormat || filename;

            link.click();
          }
        },
      }
    );
  };

  // A. General Information
  const {
    data: infoData,
    isLoading,
    refetch: refetchReviewerInfo,
  } = useQuery(['acceptance-reviewer', councilId, projectProposalId], () =>
    getReviewerInfo(projectProposalId, councilId)
  );
  const info = useMemo(() => infoData?.data, [infoData]);

  const { data: acceptanceReviewData, isLoading: isLoadingAcceptanceReview } = useQuery(
    ['acceptance-review', councilMemberId, councilId, projectProposalId],
    () => getReviewDetail(councilMemberId, projectProposalId, councilId),
    {
      enabled: !fromInternal,
    }
  );

  useEffect(() => {
    const researchTypeCode = info?.projectProposal?.researchType?.code;
    if (researchTypeCode) {
      setValue('score.researchTypeCode', researchTypeCode);
    }
  }, [info]);
  // #endregion

  // #region Events
  const onSubmit = async (data) => {
    if (!validateSignature(info?.user, t)) return;
    const reviewMeet = data?.reviewMeet;
    const reviewProduct = data?.reviewProduct;
    const reviewReportQuality = data?.reviewReportQuality;
    const reviewConclusion = data?.reviewConclusion;
    const scoreTarget = data?.score?.scoreTarget;
    const scoreContent = data?.score?.scoreContent;
    const scoreApproach = data?.score?.scoreApproach;
    const scoreProcess = data?.score?.scoreProcess;
    const scoreApplicationProduct = data?.score?.scoreApplicationProduct;
    const scorePublishedProduct = data?.score?.scorePublishedProduct;
    const scoreTrainingProduct = data?.score?.scoreTrainingProduct;
    const scoreReportQuality = data?.score?.scoreReportQuality;

    const submitData = {
      projectProposalId,
      councilId,
      projectProposalCouncilMemberId: councilMemberId,
      reviewMeet,
      reviewProduct,
      reviewReportQuality,
      reviewConclusion,
      scoreTarget,
      scoreContent,
      scoreApproach,
      scoreProcess,
      scoreApplicationProduct,
      scorePublishedProduct,
      scoreTrainingProduct,
      scoreReportQuality,
    };

    mutate(submitData, {
      onSuccess: () => {
        reset({}, { keepValues: true });
        toast(TOAST.SUCCESS, t('council.review.createSuccess'), 1000);
        setTimeout(() => {
          const queryParams = `councilId=${councilId}&councilMemberId=${councilMemberId}`;

          navigate(`/acceptance-review/edit/${projectProposalId}?${queryParams}`, {
            state: { isViewCouncilData },
            replace: true,
          });
        }, 800);
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  const {
    refetch: handleDownloadPdfTemplate,
    isLoading: isDownloadPdfTemplateLoading,
    isFetching: isDownloadPdfTemplateFetching,
  } = useQuery(
    ['downloadPdfTemplateAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () => downloadPdfTemplate({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M08 - Phieu nhan xet danh gia nghiem thu.pdf';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadDocxTemplate,
    isLoading: isDownloadDocxTemplateLoading,
    isFetching: isDownloadDocxTemplateFetching,
  } = useQuery(
    ['downloadDocxTemplateAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () => downloadDocxTemplate({ projectProposalId, councilId, councilMemberId }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M08 - Phieu nhan xet danh gia nghiem thu.docx';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      },
    }
  );

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const renderFirstTab = () => {
    if (is1593(info?.projectProposal?.template) || isLoading) {
      return (
        <DynamicAcceptanceForm
          projectProposalId={projectProposalId}
          councilId={councilId}
          councilMemberId={councilMemberId}
          generalInfo={info?.projectProposal}
          isLoading={isLoading}
          formOptions={formOptions}
        />
      );
    }
    return (
      <div className="m-0 bg-white px-4 sm:px-4 md:px-4 lg:px-6 xl:px-8 mb-3 py-5 p-fluid relative financial-review-create">
        <div className="flex gap-1 justify-content-end">
          <Button
            // type="button"
            label={t('council.review.downloadTemplate')}
            icon={`pi ${
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
                ? 'pi-spin pi-spinner'
                : 'pi-file'
            }`}
            className="block w-auto h-3rem border-none"
            severity="info"
            onClick={(event) => downloadTemplateButtonRef.current.toggle(event)}
            disabled={
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
            }
          />
        </div>
        <Menu
          ref={downloadTemplateButtonRef}
          model={[
            {
              label: t('council.review.downloadPdfTemplate'),
              icon: 'pi pi-file-pdf',
              command: handleDownloadPdfTemplate,
              disabled: isDownloadPdfTemplateLoading || isDownloadPdfTemplateFetching,
            },
            {
              label: t('council.review.downloadDocxTemplate'),
              icon: 'pi pi-file-word',
              command: handleDownloadDocxTemplate,
              disabled: isDownloadDocxTemplateLoading || isDownloadDocxTemplateFetching,
            },
          ]}
          popup
        />

        <div className="mb-5 flex justify-content-between align-items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="VNU-HCM logo"
            className="ml-3 h-4rem md:h-5rem"
          />
          <div className="pl-2">
            {t('projectProposal.viewDetail.form')} M08 <br />
            {t('council.m08.projectCode')}
          </div>
        </div>
        <div className="text-center mb-6">
          <h3 className="font-bold mb-0">{t('council.m08.title')}</h3>
          <h3 className="mt-0">{t('council.m08.sciTechProject')}</h3>
        </div>

        <DetailInfo info={info} />

        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <EvaluationForm
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            control={control}
            errors={errors}
          />

          <EvaluateTable
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            control={control}
            errors={errors}
            researchType={info?.projectProposal?.researchType}
          />

          <h5 className="font-bold">{t('council.m08.b.rank.label')}</h5>
          <ul>
            <li>{t('council.m08.b.rank.excellent')}</li>
            <li>{t('council.m08.b.rank.veryGood')}</li>
            <li>{t('council.m08.b.rank.good')}</li>
            <li>{t('council.m08.b.rank.reached')}</li>
            <li>{t('council.m08.b.rank.notReached')}</li>
          </ul>
          <div className="text-lg mt-3">
            <h5>{t('council.m08.c.conclusion')}</h5>
            <HugerteEditor name="reviewConclusion" control={control} errors={errors} />
          </div>
          <div className="grid text-lg mt-3">
            <div className="col-7 px-8">
              <span>{t('council.m01.c.commitment')}</span>
            </div>
            <div className="col-5 text-center">
              <p className="mb-0">{t('signature.date')}</p>
              <p className="font-semibold mb-0">
                {t('signature.reviewer')} <span className="text-red-500">*</span>
              </p>
              <p>{t('signature.fullnameAndSignature')}</p>
              <Signature refetchReviewerInfo={refetchReviewerInfo} reviewerInfo={info?.user} />
            </div>
          </div>

          <div className="flex justify-content-end mt-4">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              className="mr-2 w-8rem"
              onClick={() => navigate('/project-proposal-defense')}
            />
            <Button
              label={t('formLayout.action.save')}
              severity="info"
              type="submit"
              className="w-8rem"
              onClick={() => {
                if (Object.keys(errors || {}).length) {
                  toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
                }
              }}
              disabled={
                !(isDirty && Object.keys(dirtyFields).length) ||
                !isMemberAllowedReview(info?.user?.councilMembers?.[0])
              }
            />
          </div>
        </form>
      </div>
    );
  };

  if (!isLoadingAcceptanceReview && acceptanceReviewData?.data?.acceptanceReview) {
    const queryParams = `councilId=${councilId}&councilMemberId=${councilMemberId}`;

    navigate(`/acceptance-review/edit/${projectProposalId}?${queryParams}`, {
      state: { isViewCouncilData },
      replace: true,
    });
  }
  // #endregion

  return (
    <>
      {isLoadingCreate && <Loading />}

      {isErrorRes && (
        <ErrorDialog
          title={t('council.review.create')}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>

      <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
        <TabPanel header={t('council.review.createOrUpdate')} leftIcon="pi pi-file mr-2">
          {renderFirstTab()}
        </TabPanel>
        <TabPanel header={t('council.review.detailProjectProposal')} leftIcon="pi pi-list mr-2">
          <ProjectProposalDefenseDetail />
        </TabPanel>
        <TabPanel header="Nghiệm thu" leftIcon="pi pi-list mr-2">
          <ReviewFormMidtermAcceptance
            summaryReport={summaryReport}
            handleDownloadFile={handleDownloadFile}
            control={control}
            errors={errors}
          />
        </TabPanel>
        {isViewCouncilData && (
          <TabPanel header={t('council.review.councilInfo')} leftIcon="pi pi-info-circle mr-2">
            <GeneralCouncilInfo councilId={councilId} projectProposalId={projectProposalId} />
          </TabPanel>
        )}
      </TabView>
    </>
  );
}
